import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck, ShieldAlert, Save, Wand2, Copy, Download, Upload, FileCog } from 'lucide-react';
import { getToken } from '../lib/api';
import { useToast } from '../components/Toast';

const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });
const fJson = async (url: string, init: RequestInit = {}) => {
  const r = await fetch(url, { ...init, headers: { ...auth().headers, ...(init.headers || {}) } });
  if (!r.ok) throw new Error(await r.text());
  if (r.headers.get('content-type')?.includes('application/json')) return r.json();
  return r.text();
};

const STI_PA_PROVIDERS = [
  { v: 'iconectiv',    label: 'iConectiv (default STI-PA)' },
  { v: 'transunion',   label: 'TransUnion' },
  { v: 'first-orion',  label: 'First Orion' },
  { v: 'numeracle',    label: 'Numeracle' },
  { v: 'comsolve',     label: 'Comsolve' },
];

export default function StirShaken() {
  const qc = useQueryClient();
  const toast = useToast();
  const tenants = useQuery<any[]>({ queryKey: ['tenants'], queryFn: () => fJson('/api/tenants') });
  const [domain, setDomain] = useState('');
  const active = domain || tenants.data?.[0]?.domain_uuid || '';

  const cfg = useQuery<any>({
    queryKey: ['stir-shaken', active],
    queryFn: () => fJson(`/api/stir-shaken/${active}`),
    enabled: !!active,
  });

  // #1046 — cert presence + expiry parsed server-side from the stored PEM.
  const status = useQuery<any>({
    queryKey: ['stir-shaken-status', active],
    queryFn: () => fJson(`/api/stir-shaken/${active}/status`),
    enabled: !!active,
  });

  const [form, setForm] = useState<any>({});
  const f = { ...(cfg.data || {}), ...form };
  const set = (k: string, v: any) => setForm({ ...form, [k]: v });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['stir-shaken', active] });
    qc.invalidateQueries({ queryKey: ['stir-shaken-status', active] });
  };

  const save = useMutation({
    mutationFn: () => fJson(`/api/stir-shaken/${active}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(f),
    }),
    onSuccess: () => { refresh(); setForm({}); toast('success', 'Saved'); },
    onError: (e: any) => toast('error', e.message),
  });

  const genDev = useMutation({
    mutationFn: () => fJson(`/api/stir-shaken/${active}/generate-dev-cert`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    }),
    onSuccess: () => { refresh(); toast('success', 'Dev self-signed cert generated (NOT valid for production)'); },
    onError: (e: any) => toast('error', e.message),
  });

  // Regenerate /etc/kamailio/stratus_stir_shaken.cfg + per-tenant key files on
  // the API host. Kamailio reload stays a manual ops step.
  const writeFragment = useMutation({
    mutationFn: () => fJson('/api/stir-shaken/kamailio-fragment/write', { method: 'POST' }),
    onSuccess: (r: any) => { refresh(); toast('success', `Fragment written (${r.signing_tenants} signing tenant(s)) — reload Kamailio to apply`); },
    onError: (e: any) => toast('error', e.message),
  });

  // Read an uploaded .pem/.crt/.key file into the matching form field.
  const uploadPem = (field: 'cert_pem' | 'private_key_pem') => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => set(field, String(reader.result || ''));
    reader.readAsText(file);
    e.target.value = '';
  };

  async function copyKamailioSnippet() {
    try {
      const text = await fJson(`/api/stir-shaken/${active}/kamailio-snippet`);
      await navigator.clipboard.writeText(typeof text === 'string' ? text : JSON.stringify(text));
      toast('success', 'Kamailio snippet copied');
    } catch (e: any) { toast('error', e.message); }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold inline-flex items-center gap-2"><ShieldCheck size={20} /> STIR/SHAKEN</h1>
        <select className="input max-w-xs" value={active} onChange={e => setDomain(e.target.value)}>
          {(tenants.data || []).map(t => <option key={t.domain_uuid} value={t.domain_uuid}>{t.domain_description || t.domain_name}</option>)}
        </select>
      </div>

      <div className="card p-3 border-l-4" style={{ borderLeftColor: 'rgb(34 197 94)' }}>
        <div className="text-sm">
          <b>STIR/SHAKEN signing:</b> The FCC's Robocall Mitigation Database requires call-origin attestation for US carriers. Stratus signs via Kamailio's <code>secsipid</code> module per tenant.
        </div>
      </div>

      {/* Certificate status — parsed server-side from the stored PEM (#1046) */}
      <div className="card p-4">
        <div className="text-sm font-semibold mb-2 inline-flex items-center gap-2"><FileCog size={14} /> Certificate status</div>
        {!status.data ? (
          <div className="text-sm text-textSecondary">Loading…</div>
        ) : (
          <div className="flex flex-wrap gap-2 items-center text-sm">
            <StatusPill ok={!!status.data.present} okLabel="Certificate stored" badLabel="No certificate" />
            <StatusPill ok={!!status.data.key_present} okLabel="Private key stored" badLabel="No private key" />
            <StatusPill ok={!!status.data.enabled} okLabel="Signing enabled" badLabel="Signing off" neutralBad />
            {status.data.present && status.data.parse_ok === false && (
              <span className="px-2 py-0.5 rounded-full bg-red-500/15 text-red-400 inline-flex items-center gap-1"><ShieldAlert size={12} /> Stored PEM does not parse</span>
            )}
            {status.data.expires_at && (
              <span className={`px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${
                status.data.expired ? 'bg-red-500/15 text-red-400'
                : (status.data.days_until_expiry ?? 999) < 30 ? 'bg-amber-500/15 text-amber-400'
                : 'bg-elevated text-textSecondary'}`}>
                {status.data.expired
                  ? `Expired ${new Date(status.data.expires_at).toLocaleDateString()}`
                  : `Expires ${new Date(status.data.expires_at).toLocaleDateString()} (${status.data.days_until_expiry}d)`}
              </span>
            )}
            {status.data.subject && <span className="text-xs text-textSecondary font-mono truncate max-w-[28rem]" title={status.data.subject}>{status.data.subject}</span>}
          </div>
        )}
      </div>

      <div className="card p-4 grid grid-cols-2 gap-3">
        <Field label="Enabled">
          <label className="text-sm flex items-center gap-2">
            <input type="checkbox" checked={!!f.enabled} onChange={e => set('enabled', e.target.checked)} />
            Sign outbound INVITEs from this tenant
          </label>
        </Field>
        <Field label="STI-PA provider">
          <select className="input" value={f.sti_pa_provider || ''} onChange={e => set('sti_pa_provider', e.target.value || null)}>
            <option value="">— Choose —</option>
            {STI_PA_PROVIDERS.map(p => <option key={p.v} value={p.v}>{p.label}</option>)}
          </select>
        </Field>
        <Field label="Service-Provider Code (OCN)">
          <input className="input font-mono" value={f.sp_code || ''} onChange={e => set('sp_code', e.target.value)} placeholder="e.g. 123A" />
        </Field>
        <Field label="Default attestation">
          <select className="input" value={f.default_attestation || 'B'} onChange={e => set('default_attestation', e.target.value)}>
            <option value="A">A — Full attestation (we own the calling number)</option>
            <option value="B">B — Partial attestation (we know the customer but not the number)</option>
            <option value="C">C — Gateway attestation (we just transit)</option>
          </select>
        </Field>
        <Field label="Cert URL (public)">
          <input className="input font-mono" value={f.cert_url || ''} onChange={e => set('cert_url', e.target.value)} placeholder="https://certs.example.com/tenant-a.pem" />
        </Field>
        <Field label=" "><span className="text-xs text-textSecondary">Carriers fetch this URL to verify your Identity header.</span></Field>
        <div className="col-span-2">
          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm text-textSecondary">Certificate (PEM)</label>
            <label className="btn-ghost text-xs inline-flex items-center gap-1 cursor-pointer">
              <Upload size={12} /> Upload .crt/.pem
              <input type="file" accept=".pem,.crt,.cer,.cert" className="hidden" onChange={uploadPem('cert_pem')} />
            </label>
          </div>
          <textarea className="input font-mono text-xs" rows={6} value={f.cert_pem || ''} onChange={e => set('cert_pem', e.target.value)} placeholder="-----BEGIN CERTIFICATE-----..." />
        </div>
        <div className="col-span-2">
          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm text-textSecondary">Private key (PEM) — write-only, redacted on read</label>
            <label className="btn-ghost text-xs inline-flex items-center gap-1 cursor-pointer">
              <Upload size={12} /> Upload .key/.pem
              <input type="file" accept=".pem,.key" className="hidden" onChange={uploadPem('private_key_pem')} />
            </label>
          </div>
          <textarea className="input font-mono text-xs" rows={6} value={f.private_key_pem || ''} onChange={e => set('private_key_pem', e.target.value)}
            placeholder={f.private_key_pem === '[redacted]' ? '[stored — paste new value to rotate]' : '-----BEGIN PRIVATE KEY-----...'} />
        </div>
        <div className="col-span-2">
          <label className="block text-sm text-textSecondary mb-1">Notes</label>
          <input className="input" value={f.notes || ''} onChange={e => set('notes', e.target.value)} />
        </div>
        <div className="col-span-2 flex justify-between items-center mt-2">
          <div className="flex gap-2">
            <button onClick={() => genDev.mutate()} className="btn-ghost inline-flex items-center gap-2"><Wand2 size={14} /> Generate dev self-signed</button>
            <button onClick={copyKamailioSnippet} className="btn-ghost inline-flex items-center gap-2"><Copy size={14} /> Copy Kamailio snippet</button>
            <a href={`/api/stir-shaken/${active}/kamailio-snippet`} target="_blank" rel="noreferrer" className="btn-ghost inline-flex items-center gap-2"><Download size={14} /> View snippet</a>
            <button onClick={() => writeFragment.mutate()} disabled={writeFragment.isPending} className="btn-ghost inline-flex items-center gap-2" title="Regenerates /etc/kamailio/stratus_stir_shaken.cfg + per-tenant key files on the API host. Reload Kamailio afterwards.">
              <FileCog size={14} /> Write Kamailio fragment
            </button>
          </div>
          <button onClick={() => save.mutate()} className="btn-primary inline-flex items-center gap-2"><Save size={14} /> Save</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: any) {
  return <div><label className="block text-sm text-textSecondary mb-1">{label}</label>{children}</div>;
}

// Green when ok; red when bad — or muted when the "bad" state is a normal
// choice (e.g. signing simply toggled off) rather than a problem.
function StatusPill({ ok, okLabel, badLabel, neutralBad }: { ok: boolean; okLabel: string; badLabel: string; neutralBad?: boolean }) {
  const cls = ok
    ? 'bg-green-500/15 text-green-400'
    : neutralBad ? 'bg-elevated text-textSecondary' : 'bg-red-500/15 text-red-400';
  return (
    <span className={`px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${cls}`}>
      {ok ? <ShieldCheck size={12} /> : <ShieldAlert size={12} />} {ok ? okLabel : badLabel}
    </span>
  );
}
